/**
 * customer-registration.js
 *
 * Vanilla JS for the Customer Registration form.
 * Reads form values, validates, builds the exact API payload expected by
 * POST /api/customer/customer-registration, submits via fetch, and handles responses.
 *
 * API contract source: registration-migration/REGISTRATION_API.md
 * Field contract source: registration-migration/REGISTRATION_FORM.md
 *
 * IMPORTANT — CORS / API URL:
 *   The registration API base URL is injected from the section's Liquid
 *   settings via the `data-api-base` attribute on the <form> element.
 *   The .NET backend must allow CORS from the Shopify storefront origin.
 *   See registration-migration/REGISTRATION_API.md — "CORS Note".
 *
 *   Do NOT put API credentials or secrets in this file.
 */

(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /*  Environment Switch                                                  */
  /*  Set IS_PROD to true for production, false for local/dev testing.   */
  /* ------------------------------------------------------------------ */

  var IS_PROD = false; // ← toggle here

  /* ------------------------------------------------------------------ */
  /*  Constants                                                           */
  /* ------------------------------------------------------------------ */

  var DEFAULT_BRANCH_ID = '600';
  var DEFAULT_PRICE_LIBRARY_ID = '';
  var USER_PRICE_LIBRARY_ID = 'BU_LEVEL1';
  var SALES_REP_ID_PROD    = '20038';
  var SALES_REP_ID_NONPROD = '20011';
  var API_BASE_URL = 'https://custapprvlapi.jinny.com';
  var BUSINESS_SEARCH_MIN_CHARS = 3;
  var BUSINESS_SEARCH_DEBOUNCE  = 400; // ms
  var BUSINESS_SEARCH_COMPANY_ID = '1';

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  var MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB

  var ACCEPTED_MIME_TYPES = [
    'image/jpeg', 'image/png', 'image/gif', 'image/webp',
    'image/heic', 'image/heif',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ];

  var MAX_VERIFICATION_FILES = 3;
  var ACCEPTED_VERIFICATION_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
  var ACCEPTED_VERIFICATION_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'pdf'];

  /* ------------------------------------------------------------------ */
  /*  DOM helpers                                                         */
  /* ------------------------------------------------------------------ */

  function byId(id) { return document.getElementById(id); }

  function showError(fieldName, message) {
    var el = document.getElementById('creg-err-' + fieldName);
    if (!el) return;
    el.textContent = message;
    el.classList.add('creg-visible');

    var input = document.getElementById('creg-' + fieldName);
    if (input) {
      input.classList.add(input.tagName === 'SELECT' ? 'creg-select--error' : 'creg-input--error');
    }
  }

  function clearError(fieldName) {
    var el = document.getElementById('creg-err-' + fieldName);
    if (!el) return;
    el.textContent = '';
    el.classList.remove('creg-visible');

    var input = document.getElementById('creg-' + fieldName);
    if (input) {
      input.classList.remove('creg-input--error', 'creg-select--error');
    }
  }

  function showBanner(type, message) {
    var banner = byId('creg-status-banner');
    if (!banner) return;
    banner.className = 'creg-banner creg-banner--' + type + ' creg-visible';
    banner.innerHTML = message;
    banner.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  function hideBanner() {
    var banner = byId('creg-status-banner');
    if (!banner) return;
    banner.className = 'creg-banner';
    banner.textContent = '';
  }

  /* ------------------------------------------------------------------ */
  /*  Validation                                                          */
  /* ------------------------------------------------------------------ */

  /**
   * Validates a single field.
   * @param {string} name   - field name matching the form's data-name attributes
   * @param {string} value  - current value
   * @param {Object} values - all current values (needed for emailConfirm cross-check)
   * @returns {string|null} error message, or null if valid
   */
  function looksLikeEmailOrUrl(v) {
    return /@|https?:|www\.|\.(com|net|org|io|co|us|info|biz)\b|\/\//i.test(v);
  }

  function isValidUsPhone(v) {
    if (!/^[\d\s().+\-]+$/.test(v)) return false;
    var d = v.replace(/\D/g, '');
    return d.length === 10 || (d.length === 11 && d.charAt(0) === '1');
  }

  var ALWAYS_REQUIRED = ['firstName', 'lastName', 'email'];

  // Only first name, last name and email are mandatory; other fields are
  // format-checked only when the applicant filled them in.
  function validateField(name, value, values, formType) {
    var alwaysRequired = ALWAYS_REQUIRED.indexOf(name) !== -1 || (formType === 'customer' && name === 'phone');
    if (!alwaysRequired && !String(value || '').trim()) return null;
    return validateFieldRules(name, value, values, formType);
  }

  function validateFieldRules(name, value, values, formType) {
    switch (name) {
      case 'role':
        return value ? null : 'Please select your role.';
      case 'businessType':
        if (formType === 'customer' && values.hasBusiness !== 'yes') return null;
        return value ? null : 'Please select a business type.';
      case 'barberCount':
        if (formType === 'customer' && !(values.hasBusiness === 'yes' && values.businessType === 'barbershop')) return null;
        return (value !== '' && Number(value) >= 0) ? null : 'Please enter the number of barbers.';
      case 'currentlyRetailsProducts':
        if (formType === 'customer' && !(values.hasBusiness === 'yes' && values.businessType === 'barbershop')) return null;
        return (value === 'yes' || value === 'no') ? null : 'Please let us know if you currently retail products.';
      case 'whereShopNow':
        if (formType === 'customer' && !(values.hasBusiness === 'yes' && values.businessType === 'barbershop' && values.currentlyRetailsProducts === 'yes')) return null;
        return value.trim() ? null : 'Please tell us where you currently shop.';
      case 'interestedInSellingProducts':
        if (formType === 'customer' && !(values.hasBusiness === 'yes' && values.businessType === 'barbershop' && values.currentlyRetailsProducts === 'no')) return null;
        return (value === 'yes' || value === 'no') ? null : 'Please let us know if you are interested in selling our products.';
      case 'shopFrequency':
        if (formType === 'customer' && !(values.hasBusiness === 'yes' && values.businessType === 'barbershop')) return null;
        return value ? null : 'Please select how often you shop.';
      case 'shopArrangement':
        if (formType === 'customer' && !(values.hasBusiness === 'yes' && values.businessType === 'barbershop')) return null;
        return value ? null : 'Please select your shop arrangement.';
      case 'firstName':
        if (!value.trim()) return 'First name is required.';
        return looksLikeEmailOrUrl(value) ? 'Please enter a valid first name.' : null;
      case 'lastName':
        if (!value.trim()) return 'Last name is required.';
        return looksLikeEmailOrUrl(value) ? 'Please enter a valid last name.' : null;
      case 'phone':
        if (!value.trim()) return formType === 'customer' ? 'Phone is required.' : null; // optional for other flows
        return isValidUsPhone(value) ? null : 'Please enter a valid US phone number (10 digits).';
      case 'email':
        if (!value.trim()) return 'Email is required.';
        if (!EMAIL_RE.test(value.trim())) return 'Please enter a valid email address.';
        return null;
      case 'emailConfirm':
        if (!value.trim()) return 'Please confirm your email.';
        if (value.trim() !== (values.email || '').trim()) return 'Email addresses do not match.';
        return null;
      case 'hasBusiness':
        return (value === 'yes' || value === 'no') ? null : 'Please let us know if you own or represent a business.';
      case 'businessName':
        if (formType === 'customer' && values.hasBusiness !== 'yes') return null;
        return value.trim() ? null : 'Business name is required.';
      case 'streetAddress':
        if (!value.trim()) return 'Street address is required.';
        return value.trim().length > 150 ? 'Street address must be 150 characters or fewer.' : null;
      case 'city':
        if (!value.trim()) return 'City is required.';
        return /^[\d\s\-.,]+$/.test(value.trim()) ? 'Please enter a valid city.' : null;
      case 'stateProvince':
        if (formType === 'customer') return value.trim() ? null : 'State / Province / Region is required.';
        return null;
      case 'postalCode':
        if (!value.trim()) return 'Postal / Zip code is required.';
        if ((values.country || 'US') === 'US' && !/^\d{5}(-\d{4})?$/.test(value.trim())) {
          return 'Please enter a valid ZIP code (12345 or 12345-6789).';
        }
        return null;
      case 'country':
        return value ? null : 'Please select a country.';
      case 'isOperational':
        return value ? null : 'Please indicate if your business is operational.';
      case 'monthlySpend':
        return value ? null : 'Please select anticipated monthly spending.';
      case 'yearsInBusiness':
        return value ? null : 'Please select how long you\'ve been in business.';
      case 'businessClassification':
        return value ? null : 'Please select a business classification.';
      case 'referralSource':
        return value ? null : 'Please let us know how you found us.';
      default:
        return null; // streetAddress1, taxId, businessLicense, salesTaxId — optional
    }
  }

  var REQUIRED_FIELDS_CUSTOMER = ['firstName', 'lastName', 'phone', 'email'];

  var REQUIRED_FIELDS_USER = ALWAYS_REQUIRED;

  function getRequiredFields(formType) {
    return formType === 'user' ? REQUIRED_FIELDS_USER : REQUIRED_FIELDS_CUSTOMER;
  }

  /**
   * Collects all form values, validates every required field.
   * Address fields (streetAddress, city, stateProvince, postalCode, country)
   * are always required for the "customer" (wholesale) form. Business-name
   * fields (businessName) are only enforced when the applicant answered
   * "Yes" to "Do you own or represent a business?".
   * @param {HTMLFormElement} form
   * @returns {{ valid: boolean, values: Object, errors: Object }}
   */
  function validateAll(form, formType) {
    var values = collectValues(form);
    var errors = {};
    var valid = true;
    var requiredFields = getRequiredFields(formType);

    var fieldNames = requiredFields.concat(Object.keys(values).filter(function (n) {
      return requiredFields.indexOf(n) === -1;
    }));

    fieldNames.forEach(function (name) {
      var err = validateField(name, values[name] || '', values, formType);
      if (err) {
        errors[name] = err;
        valid = false;
      }
    });

    return { valid: valid, values: values, errors: errors, requiredFields: fieldNames };
  }

  /* ------------------------------------------------------------------ */
  /*  Value collection                                                    */
  /* ------------------------------------------------------------------ */

  function collectValues(form) {
    var values = {};
    var elements = form.querySelectorAll('[data-creg-field]');
    elements.forEach(function (el) {
      var name = el.getAttribute('data-creg-field');
      values[name] = (el.type === 'checkbox') ? el.checked : el.value;
    });
    return values;
  }

  /* ------------------------------------------------------------------ */
  /*  Build API payload                                                   */
  /* ------------------------------------------------------------------ */

  /**
   * Builds the payload for the legacy Customer Registration flow
   * (retained for the "user" form type — see buildUserPayload below).
   * Preserves exact property names from REGISTRATION_API.md / REGISTRATION_TYPES.md.
   */
  function buildLegacyPayload(values, fileState) {
  const isProd = IS_PROD;

  var payload = {
    role:                   values.role,
    firstName:              values.firstName.trim(),
    lastName:               values.lastName.trim(),
    email:                  values.email.trim(),

    businessName:           values.businessName.trim(),
    businessType:           values.businessType,

    streetAddress:          values.streetAddress.trim(),
    streetAddress1:         (values.streetAddress1 || '').trim(),
    city:                   values.city.trim(),
    postalCode:             values.postalCode.trim(),
    country:                values.country,

    isOperational:          values.isOperational,
    monthlySpend:           values.monthlySpend,
    yearsInBusiness:        values.yearsInBusiness,
    businessClassification: values.businessClassification,
    referralSource:         values.referralSource,

    branchId:               DEFAULT_BRANCH_ID,
    priceLibraryId:         DEFAULT_PRICE_LIBRARY_ID,

    selectedCustomerId:     (values.selectedCustomerId || '').trim() || null,

    // Automatically determined by environment
    salesRepId:             isProd
      ? SALES_REP_ID_PROD
      : SALES_REP_ID_NONPROD,

    termsId:                (values.termsId || '').trim() || null,
    serviceTermsId:         (values.serviceTermsId || '').trim() || null,

    isProd:                 isProd,

    documents:              [],
    submittedAt:            new Date().toISOString()
  };

  // Optional fields — omit when blank
  var phone = (values.phone || '').trim();
  if (phone) {
    payload.phone = phone;
  }

  var stateProvince = (values.stateProvince || '').trim();
  if (stateProvince) {
    payload.stateProvince = stateProvince;
  }

  var businessLicense = (values.businessLicense || '').trim();
  if (businessLicense) {
    payload.businessLicense = businessLicense;
  }

  var salesTaxId = (values.salesTaxId || '').trim();
  if (salesTaxId) {
    payload.salesTaxId = salesTaxId;
  }

  // Supporting document placeholder
  if (fileState.file) {
    payload.documents = [{
      documentType: 'supportingDocument',
      fileName: fileState.file.name,
      filePath: ''
    }];
  }

  return payload;
}

  /**
   * Builds the payload for the User Registration flow.
   * Role and Business Details fields are not present in the user form —
   * strip them out and set safe defaults before sending to the API.
   */
  function buildUserPayload(values, fileState) {
    var payload = buildLegacyPayload(values, fileState);
    payload.priceLibraryId = USER_PRICE_LIBRARY_ID;
    delete payload.role;
    delete payload.isOperational;
    delete payload.monthlySpend;
    delete payload.yearsInBusiness;
    delete payload.businessClassification;
    delete payload.businessLicense;
    delete payload.salesTaxId;
    delete payload.referralSource;
    return payload;
  }

  /**
   * Builds the payload for the redesigned Wholesale Customer Registration form.
   *
   * NOTE — API / DTO changes required on the backend to fully support this form:
   *   - "role" is replaced by "businessType" with new enum values:
   *       barber | barbershop | retail | wholesaler | other
   *     (previously: owner | manager | buyer | sales | other)
   *   - "phone" is now always sent and required (previously optional).
   *   - New field "hasBusiness" (boolean) indicates whether the applicant
   *     owns/represents a business.
   *   - New optional field "taxId" (EIN / Tax ID).
   *   - "businessName" / address fields are omitted (not required) when
   *     hasBusiness is false.
   *   - "documents" now may contain up to 3 entries with
   *     documentType "businessVerification" (previously a single
   *     "supportingDocument").
   *   - Legacy fields no longer sent: isOperational, monthlySpend,
   *     yearsInBusiness, businessClassification, referralSource,
   *     businessLicense, salesTaxId (these were never wired into the UI).
   */
  function buildWholesalePayload(values, attachments) {
    var isProd = IS_PROD;
    var hasBusiness = values.hasBusiness === 'yes';

    var payload = {
      businessType:       values.businessType,
      firstName:          values.firstName.trim(),
      lastName:           values.lastName.trim(),
      phone:              (values.phone || '').trim(),
      email:              values.email.trim(),

      hasBusiness:        hasBusiness,

      branchId:           DEFAULT_BRANCH_ID,
      priceLibraryId:     DEFAULT_PRICE_LIBRARY_ID,

      selectedCustomerId: (values.selectedCustomerId || '').trim() || null,

      // Automatically determined by environment
      salesRepId:         isProd ? SALES_REP_ID_PROD : SALES_REP_ID_NONPROD,

      termsId:            (values.termsId || '').trim() || null,
      serviceTermsId:     (values.serviceTermsId || '').trim() || null,

      isProd:             isProd,

      documents:          (attachments || []).map(function (a) {
        return { documentType: 'businessVerification', fileName: a.file.name, filePath: '' };
      }),
      submittedAt:        new Date().toISOString()
    };

    if (hasBusiness) {
      payload.businessName   = (values.businessName || '').trim();
      payload.streetAddress  = (values.streetAddress || '').trim();
      payload.streetAddress1 = (values.streetAddress1 || '').trim();
      payload.city            = (values.city || '').trim();
      payload.stateProvince   = (values.stateProvince || '').trim();
      payload.postalCode      = (values.postalCode || '').trim();
      payload.country         = values.country;

      var taxId = (values.taxId || '').trim();
      if (taxId) payload.taxId = taxId;
    }

    return payload;
  }


  /**
   * Routes to the correct payload builder based on form type.
   * formType: 'customer' | 'user'
   */
  function buildPayload(formType, values, fileState, attachments) {
    if (formType === 'user') return buildUserPayload(values, fileState);
    if (formType === 'customer') return buildWholesalePayload(values, attachments);
    return buildLegacyPayload(values, fileState);
  }

  /* ------------------------------------------------------------------ */
  /*  Verification attachments (max 3 per portion) — "customer" form      */
  /*  Two independent portions:                                           */
  /*    - "barber"   — always shown (barber/school/social-media docs)     */
  /*    - "business" — only shown when hasBusiness === 'yes'               */
  /*  At least one document is required across BOTH portions combined.    */
  /* ------------------------------------------------------------------ */

  function isAcceptedVerificationFile(file) {
    var ext = (file.name.split('.').pop() || '').toLowerCase();
    if (ACCEPTED_VERIFICATION_EXTENSIONS.indexOf(ext) !== -1) return true;
    return ACCEPTED_VERIFICATION_MIME_TYPES.indexOf(file.type) !== -1;
  }

  function formatFileSize(bytes) {
    return (bytes / 1024 < 1024)
      ? (bytes / 1024).toFixed(1) + ' KB'
      : (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  /**
   * Creates an independent file-upload controller (drag/drop + click +
   * validation + rendering) bound to its own set of DOM ids and its own
   * attachments array.
   */
  function createVerificationUploader(opts) {
    var attachments = []; // [{ file: File }]

    function getAttachments() { return attachments; }

    function renderList(form) {
      var list = form.querySelector('#' + opts.listId);
      if (!list) return;
      list.innerHTML = '';

      attachments.forEach(function (attachment, index) {
        var item = document.createElement('div');
        item.className = 'creg-verification-file-item';

        var icon = /image\//.test(attachment.file.type) || /\.(jpe?g|png|webp)$/i.test(attachment.file.name) ? '🖼️' : '📄';

        item.innerHTML =
          '<span class="creg-verification-file-icon" aria-hidden="true">' + icon + '</span>' +
          '<span class="creg-verification-file-name">' + escHtml(attachment.file.name) + '</span>' +
          '<span class="creg-verification-file-size">' + formatFileSize(attachment.file.size) + '</span>' +
          '<button type="button" class="creg-file-remove" data-index="' + index + '" aria-label="Remove ' + escHtml(attachment.file.name) + '">&#10005;</button>';

        list.appendChild(item);
      });

      list.querySelectorAll('.creg-file-remove').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var idx = parseInt(btn.getAttribute('data-index'), 10);
          attachments.splice(idx, 1);
          clearError(opts.fieldName);
          clearError('verificationFiles');
          renderList(form);
        });
      });
    }

    function init(form) {
      var input = form.querySelector('#' + opts.inputId);
      var zone  = form.querySelector('#' + opts.zoneId);
      if (!input || !zone) return;

      zone.addEventListener('click', function (e) {
        if (e.target.closest && e.target.closest('.creg-file-remove')) return;
        input.click();
      });

      zone.addEventListener('dragover', function (e) {
        e.preventDefault();
        zone.style.borderColor = '#2563eb';
      });
      zone.addEventListener('dragleave', function () { zone.style.borderColor = ''; });
      zone.addEventListener('drop', function (e) {
        e.preventDefault();
        zone.style.borderColor = '';
        addFiles(e.dataTransfer ? e.dataTransfer.files : []);
      });

      input.addEventListener('change', function () {
        addFiles(input.files);
        input.value = '';
      });

      function addFiles(fileList) {
        clearError(opts.fieldName);
        clearError('verificationFiles');
        zone.classList.remove('creg-file-zone--error');

        var files = Array.prototype.slice.call(fileList || []);
        for (var i = 0; i < files.length; i++) {
          var file = files[i];

          if (attachments.length >= MAX_VERIFICATION_FILES) {
            showError(opts.fieldName, 'You can upload a maximum of ' + MAX_VERIFICATION_FILES + ' files. Remove a file before adding another.');
            zone.classList.add('creg-file-zone--error');
            break;
          }

          if (!isAcceptedVerificationFile(file)) {
            showError(opts.fieldName, '"' + file.name + '" is not a supported file type. Please upload JPG, JPEG, PNG, WebP, or PDF files.');
            zone.classList.add('creg-file-zone--error');
            continue;
          }

          if (file.size > MAX_FILE_BYTES) {
            showError(opts.fieldName, '"' + file.name + '" is too large. Maximum file size is 10 MB.');
            zone.classList.add('creg-file-zone--error');
            continue;
          }

          attachments.push({ file: file });
        }

        renderList(form);
      }
    }

    function reset(form) {
      attachments = [];
      renderList(form);
      clearError(opts.fieldName);
      var zone = form.querySelector('#' + opts.zoneId);
      if (zone) zone.classList.remove('creg-file-zone--error');
    }

    return { getAttachments: getAttachments, init: init, reset: reset };
  }

  var barberVerificationUploader = createVerificationUploader({
    fieldName: 'barberVerificationFiles',
    inputId:   'creg-barberVerificationFiles',
    zoneId:    'creg-barber-verification-zone',
    listId:    'creg-barber-verification-file-list'
  });

  var businessVerificationUploader = createVerificationUploader({
    fieldName: 'businessVerificationFiles',
    inputId:   'creg-businessVerificationFiles',
    zoneId:    'creg-business-verification-zone',
    listId:    'creg-business-verification-file-list'
  });

  /**
   * Combined attachments across both portions, honoring visibility —
   * business documents only count while the applicant owns/represents a
   * business (i.e. while that portion is shown).
   */
  function getAllVerificationAttachments(values) {
    var all = barberVerificationUploader.getAttachments().slice();
    if (values.hasBusiness === 'yes') {
      all = all.concat(businessVerificationUploader.getAttachments());
    }
    return all;
  }

  function validateAttachments(values) {
    if (getAllVerificationAttachments(values).length === 0) {
      return 'Please upload at least one verification document.';
    }
    return null;
  }

  function initVerificationUploads(form) {
    barberVerificationUploader.init(form);
    businessVerificationUploader.init(form);
  }

  function resetVerificationAttachments(form) {
    barberVerificationUploader.reset(form);
    businessVerificationUploader.reset(form);
    clearError('verificationFiles');
  }

  /* ------------------------------------------------------------------ */
  /*  "Do you own or represent a business?" toggle — wholesale form       */
  /* ------------------------------------------------------------------ */

  function setHasBusinessValue(form, value) {
    var hidden  = form.querySelector('#creg-hasBusiness');
    var section = form.querySelector('#creg-business-information-section');
    var toggle  = form.querySelector('#creg-hasBusiness-toggle');
    if (!hidden) return;

    hidden.value = value;

    if (toggle) {
      toggle.querySelectorAll('.creg-toggle-btn').forEach(function (btn) {
        var isActive = btn.getAttribute('data-toggle-value') === value;
        btn.classList.toggle('creg-toggle-btn--active', isActive);
        btn.setAttribute('aria-checked', isActive ? 'true' : 'false');
      });
    }

    // The entire "4. Business Information" section (header + fields) stays
    // hidden until the applicant selects "Yes".
    if (section) section.hidden = (value !== 'yes');

    clearError('hasBusiness');
    touched.hasBusiness = true;
    clearError('verificationFiles');

    refreshBarbershopVisibility(form);
  }

  function initHasBusinessToggle(form) {
    var toggle = form.querySelector('#creg-hasBusiness-toggle');
    var businessTypeSelect = form.querySelector('#creg-businessType');
    if (!toggle) return;

    toggle.querySelectorAll('.creg-toggle-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        setHasBusinessValue(form, btn.getAttribute('data-toggle-value'));
      });
    });

    // Business Type (Barbershop / Salon Owner vs. Wholesaler / Distributor)
    // only appears once "Yes" is selected above; changing it toggles the
    // Barbershop-specific questions section.
    if (businessTypeSelect) {
      businessTypeSelect.addEventListener('change', function () {
        refreshBarbershopVisibility(form);
      });
    }

    // Default to "No" until the applicant chooses otherwise, matching the
    // pre-checked state in markup.
    setHasBusinessValue(form, 'no');
    touched.hasBusiness = false;
  }

  /* ------------------------------------------------------------------ */
  /*  Generic Yes/No toggle groups (Barbershop / Salon Owner questions)   */
  /* ------------------------------------------------------------------ */

  function setToggleValue(form, fieldName, value) {
    var hidden = form.querySelector('#creg-' + fieldName);
    var toggle = form.querySelector('#creg-' + fieldName + '-toggle');
    if (!hidden) return;

    hidden.value = value;

    if (toggle) {
      toggle.querySelectorAll('.creg-toggle-btn').forEach(function (btn) {
        var isActive = btn.getAttribute('data-toggle-value') === value;
        btn.classList.toggle('creg-toggle-btn--active', isActive);
        btn.setAttribute('aria-checked', isActive ? 'true' : 'false');
      });
    }

    clearError(fieldName);
    touched[fieldName] = true;
  }

  function initToggleGroup(form, fieldName, onChange) {
    var toggle = form.querySelector('#creg-' + fieldName + '-toggle');
    if (!toggle) return;

    toggle.querySelectorAll('.creg-toggle-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var value = btn.getAttribute('data-toggle-value');
        setToggleValue(form, fieldName, value);
        if (onChange) onChange(value);
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Barbershop / Salon Owner Questions — conditional visibility         */
  /* ------------------------------------------------------------------ */

  function refreshBarbershopVisibility(form) {
    var hasBusinessEl  = form.querySelector('#creg-hasBusiness');
    var businessTypeEl = form.querySelector('#creg-businessType');
    var section        = form.querySelector('#creg-barbershop-questions');
    if (!section) return;

    var show = !!hasBusinessEl && hasBusinessEl.value === 'yes' &&
      !!businessTypeEl && businessTypeEl.value === 'barbershop';

    section.hidden = !show;
  }

  function refreshRetailSubFields(form) {
    var currentlyRetailsEl = form.querySelector('#creg-currentlyRetailsProducts');
    var whereWrap           = form.querySelector('#creg-whereShopNow-wrap');
    var interestedWrap      = form.querySelector('#creg-interestedInSellingProducts-wrap');
    if (!currentlyRetailsEl) return;

    var value = currentlyRetailsEl.value;
    if (whereWrap) whereWrap.hidden = value !== 'yes';
    if (interestedWrap) interestedWrap.hidden = value !== 'no';
  }

  function initBarbershopQuestions(form) {
    initToggleGroup(form, 'currentlyRetailsProducts', function () {
      refreshRetailSubFields(form);
    });
    initToggleGroup(form, 'interestedInSellingProducts');

    refreshBarbershopVisibility(form);
    refreshRetailSubFields(form);
  }

  /* ------------------------------------------------------------------ */
  /*  File state                                                          */
  /* ------------------------------------------------------------------ */

  var fileState = { file: null };

  function initFileInput(form) {
    var zone       = form.querySelector('.creg-file-zone');
    var fileInput  = byId('creg-supportingFile');
    var preview    = byId('creg-file-preview');
    var nameEl     = byId('creg-file-name');
    var sizeEl     = byId('creg-file-size');
    var removeBtn  = byId('creg-file-remove');
    var fileErrEl  = byId('creg-err-supportingFile');

    if (!zone || !fileInput) return;

    zone.addEventListener('click', function (e) {
      if (e.target === removeBtn) return;
      fileInput.click();
    });

    zone.addEventListener('dragover', function (e) {
      e.preventDefault();
      zone.style.borderColor = '#2563eb';
    });

    zone.addEventListener('dragleave', function () {
      zone.style.borderColor = '';
    });

    zone.addEventListener('drop', function (e) {
      e.preventDefault();
      zone.style.borderColor = '';
      var file = e.dataTransfer && e.dataTransfer.files[0];
      if (file) handleFile(file);
    });

    fileInput.addEventListener('change', function () {
      if (fileInput.files[0]) handleFile(fileInput.files[0]);
    });

    if (removeBtn) {
      removeBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        clearFile();
      });
    }

    function handleFile(file) {
      if (fileErrEl) { fileErrEl.textContent = ''; fileErrEl.classList.remove('creg-visible'); }
      zone.classList.remove('creg-file-zone--error');

      if (file.size > MAX_FILE_BYTES) {
        var mb = (file.size / (1024 * 1024)).toFixed(1);
        if (fileErrEl) { fileErrEl.textContent = 'File is too large (' + mb + ' MB). Maximum allowed size is 10.0 MB.'; fileErrEl.classList.add('creg-visible'); }
        zone.classList.add('creg-file-zone--error');
        return;
      }

      if (ACCEPTED_MIME_TYPES.indexOf(file.type) === -1) {
        if (fileErrEl) { fileErrEl.textContent = 'Unsupported file type. Please upload an image (JPEG, PNG, GIF, WEBP, HEIC) or a document (PDF, DOC, DOCX).'; fileErrEl.classList.add('creg-visible'); }
        zone.classList.add('creg-file-zone--error');
        return;
      }

      fileState.file = file;

      if (nameEl) nameEl.textContent = file.name;
      if (sizeEl) sizeEl.textContent = (file.size / 1024 < 1024)
        ? (file.size / 1024).toFixed(1) + ' KB'
        : (file.size / (1024 * 1024)).toFixed(1) + ' MB';

      if (preview) preview.style.display = 'flex';
      zone.querySelector('.creg-file-zone-label') && (zone.querySelector('.creg-file-zone-label').style.display = 'none');
      zone.querySelector('.creg-file-zone-sub') && (zone.querySelector('.creg-file-zone-sub').style.display = 'none');
    }

    function clearFile() {
      fileState.file = null;
      fileInput.value = '';
      if (preview) preview.style.display = 'none';
      zone.querySelector('.creg-file-label') && (zone.querySelector('.creg-file-label').style.display = '');
      zone.querySelector('.creg-file-zone-label') && (zone.querySelector('.creg-file-zone-label').style.display = '');
      zone.querySelector('.creg-file-zone-sub') && (zone.querySelector('.creg-file-zone-sub').style.display = '');
      if (fileErrEl) { fileErrEl.textContent = ''; fileErrEl.classList.remove('creg-visible'); }
      zone.classList.remove('creg-file-zone--error');
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Blur validation                                                     */
  /* ------------------------------------------------------------------ */

  var touched = {};

  function attachBlurHandlers(form, formType) {
    form.querySelectorAll('[data-creg-field]').forEach(function (el) {
      el.addEventListener('blur', function () {
        var name = el.getAttribute('data-creg-field');
        touched[name] = true;
        var values = collectValues(form);
        var err = validateField(name, el.value, values, formType);
        if (err) showError(name, err);
        else clearError(name);
      });

      el.addEventListener('input', function () {
        if (!touched[el.getAttribute('data-creg-field')]) return;
        var name = el.getAttribute('data-creg-field');
        var values = collectValues(form);
        var err = validateField(name, el.value, values, formType);
        if (err) showError(name, err);
        else clearError(name);
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Predictive business search                                          */
  /* ------------------------------------------------------------------ */

  var businessSearchState = {
    debounceTimer: null
  };

  function clearBusinessSelection(form) {
    setFieldValue(form, 'creg-selectedCustomerId', '');
    setFieldValue(form, 'creg-salesRepId',         false ? SALES_REP_ID_PROD : SALES_REP_ID_NONPROD);
    setFieldValue(form, 'creg-termsId',            '');
    setFieldValue(form, 'creg-serviceTermsId',     '');
    var badge = form.querySelector('#creg-business-selected');
    if (badge) { badge.style.display = 'none'; badge.innerHTML = ''; }
  }

  function setFieldValue(form, id, value) {
    if (!value) return;
    var el = form.querySelector('#' + id);
    if (!el) return;
    el.value = value;
    el.dispatchEvent(new Event('input',  { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function populateBusinessFields(form, data) {
    setFieldValue(form, 'creg-businessName',        data.businessName);
    setFieldValue(form, 'creg-streetAddress',       data.streetAddress);
    setFieldValue(form, 'creg-streetAddress1',      data.streetAddress2);
    setFieldValue(form, 'creg-city',                data.city);
    setFieldValue(form, 'creg-stateProvince',       data.state);
    setFieldValue(form, 'creg-postalCode',          data.postalCode);
    setFieldValue(form, 'creg-country',             data.country);
    setFieldValue(form, 'creg-phone',               data.phone);
    setFieldValue(form, 'creg-selectedCustomerId',  data.customerId);
    setFieldValue(form, 'creg-salesRepId',          data.salesRepId);
    setFieldValue(form, 'creg-termsId',             data.termsId);
    setFieldValue(form, 'creg-serviceTermsId',      data.serviceTermsId);
    setFieldValue(form, 'creg-priceLibraryId',      data.priceLibraryId);
  }

  function fetchCustomerDetail(customerId, apiBase, form, badge) {
    var url = (apiBase || '').replace(/\/$/, '') +
      '/api/p21/customer/' + encodeURIComponent(customerId) + '?isProd=' + IS_PROD;

    fetch(url, { method: 'GET', headers: { 'Content-Type': 'application/json' } })
      .then(function (res) { return res.json().then(function (d) { return { ok: res.ok, data: d }; }); })
      .then(function (r) {
        if (!r.ok || !r.data.success) {
          if (badge) {
            badge.innerHTML =
              '<span class="creg-business-selected-icon creg-business-selected-icon--error">&#9888;</span>' +
              '<span>Could not load customer details. You may continue or select another customer.</span>' +
              '<button type="button" class="creg-business-selected-clear" aria-label="Clear selection">&#x2715;</button>';
            badge.style.display = 'flex';
            var clearBtn = badge.querySelector('.creg-business-selected-clear');
            if (clearBtn) clearBtn.addEventListener('click', function () {
              clearBusinessSelection(form);
              var input = form.querySelector('#creg-businessName');
              if (input) { input.value = ''; input.focus(); }
            });
          }
          return;
        }

        var detail = r.data.data;
        populateBusinessFields(form, detail);

        if (badge) {
          badge.innerHTML =
            '<span class="creg-business-selected-icon">&#10003;</span>' +
            '<span>Existing customer selected — <strong>' + escHtml(detail.businessName || '') + '</strong></span>' +
            '<button type="button" class="creg-business-selected-clear" aria-label="Clear selection">&#x2715;</button>';
          badge.style.display = 'flex';
          var clearBtn = badge.querySelector('.creg-business-selected-clear');
          if (clearBtn) clearBtn.addEventListener('click', function () {
            clearBusinessSelection(form);
            var input = form.querySelector('#creg-businessName');
            if (input) { input.value = ''; input.focus(); }
          });
        }
      })
      .catch(function () {
        if (badge) {
          badge.innerHTML =
            '<span class="creg-business-selected-icon creg-business-selected-icon--error">&#9888;</span>' +
            '<span>Unable to retrieve customer details. Please check your connection.</span>' +
            '<button type="button" class="creg-business-selected-clear" aria-label="Clear selection">&#x2715;</button>';
          badge.style.display = 'flex';
          var clearBtn = badge.querySelector('.creg-business-selected-clear');
          if (clearBtn) clearBtn.addEventListener('click', function () {
            clearBusinessSelection(form);
            var input = form.querySelector('#creg-businessName');
            if (input) { input.value = ''; input.focus(); }
          });
        }
      });
  }

  function selectBusiness(form, customer) {
    closeBusinessDropdown(form);

    var badge = form.querySelector('#creg-business-selected');
    if (badge) {
      badge.innerHTML =
        '<span class="creg-business-selected-icon">&#8987;</span>' +
        '<span>Loading customer details\u2026</span>';
      badge.style.display = 'flex';
    }

    var apiBase = (form.getAttribute('data-api-base') || API_BASE_URL);
    fetchCustomerDetail(customer.customerId, apiBase, form, badge);
  }

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function closeBusinessDropdown(form) {
    var dropdown = form.querySelector('#creg-business-dropdown');
    var input    = form.querySelector('#creg-businessName');
    if (dropdown) { dropdown.innerHTML = ''; dropdown.classList.remove('creg-business-dropdown--open'); }
    if (input)    { input.setAttribute('aria-expanded', 'false'); }
  }

  function renderBusinessDropdown(form, state, results) {
    var dropdown = form.querySelector('#creg-business-dropdown');
    var input    = form.querySelector('#creg-businessName');
    if (!dropdown) return;

    dropdown.innerHTML = '';

    if (state === 'short') {
      dropdown.innerHTML = '<div class="creg-biz-message">Please enter at least ' + BUSINESS_SEARCH_MIN_CHARS + ' characters to search.</div>';
      dropdown.classList.add('creg-business-dropdown--open');
      if (input) input.setAttribute('aria-expanded', 'true');
      return;
    }

    if (state === 'loading') {
      dropdown.innerHTML = '<div class="creg-biz-message">Searching businesses\u2026</div>';
      dropdown.classList.add('creg-business-dropdown--open');
      if (input) input.setAttribute('aria-expanded', 'true');
      return;
    }

    if (state === 'error') {
      dropdown.innerHTML = '<div class="creg-biz-message creg-biz-message--error">Unable to search businesses. Please try again.</div>';
      dropdown.classList.add('creg-business-dropdown--open');
      if (input) input.setAttribute('aria-expanded', 'true');
      return;
    }

    if (state === 'empty') {
      dropdown.innerHTML = '<div class="creg-biz-message">No existing businesses found.</div>';
      dropdown.classList.add('creg-business-dropdown--open');
      if (input) input.setAttribute('aria-expanded', 'true');
      return;
    }

    // state === 'results'
    results.forEach(function (customer) {
      var item = document.createElement('div');
      item.className   = 'creg-biz-item';
      item.setAttribute('role', 'option');
      item.setAttribute('tabindex', '0');
      item.innerHTML =
        '<span class="creg-biz-name">'     + escHtml(customer.businessName) + '</span>' +
        '<span class="creg-biz-location">' + escHtml((customer.city || '') + (customer.state ? ', ' + customer.state : '')) + '</span>' +
        '<span class="creg-biz-location">' + escHtml(customer.streetAddress || '') + '</span>';

      function doSelect() { selectBusiness(form, customer); }
      item.addEventListener('click', doSelect);
      item.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); doSelect(); }
      });

      dropdown.appendChild(item);
    });

    dropdown.classList.add('creg-business-dropdown--open');
    if (input) input.setAttribute('aria-expanded', 'true');
  }

  function searchBusinesses(term, apiBase, form) {
    renderBusinessDropdown(form, 'loading', []);

    var url = (apiBase || '').replace(/\/$/, '') +
      '/api/p21/customer/search' +
      '?searchTerm=' + encodeURIComponent(term) +
      '&isProd=' + IS_PROD +
      '&companyId=' + BUSINESS_SEARCH_COMPANY_ID;

    console.log('[BusinessSearch] Searching for "' + term + '"');
    console.log('[BusinessSearch] GET ' + url);

    fetch(url, { method: 'GET', headers: { 'Content-Type': 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (body) {
        var results = (body && body.success && Array.isArray(body.data)) ? body.data : [];
        console.log('[BusinessSearch] Results: ' + results.length + ' found', results);
        if (results.length === 0) {
          renderBusinessDropdown(form, 'empty', []);
        } else {
          renderBusinessDropdown(form, 'results', results);
        }
      })
      .catch(function (err) {
        console.error('[BusinessSearch] Request failed:', err);
        renderBusinessDropdown(form, 'error', []);
      });
  }

  function initBusinessSearch(form, apiBase) {
    var input     = form.querySelector('#creg-businessName');
    var searchBtn = form.querySelector('#creg-business-search-btn');
    if (!input) return;

    // Close dropdown when clicking outside
    document.addEventListener('click', function (e) {
      var wrap = form.querySelector('.creg-business-search-wrap');
      if (wrap && !wrap.contains(e.target)) closeBusinessDropdown(form);
    });

    // Clear selection when user edits the field
    input.addEventListener('input', function () {
      clearBusinessSelection(form);
      closeBusinessDropdown(form);
    });

    function doSearch() {
      var term = input.value.trim();
      if (term.length < BUSINESS_SEARCH_MIN_CHARS) {
        closeBusinessDropdown(form);
        renderBusinessDropdown(form, 'short', []);
        return;
      }
      searchBusinesses(term, apiBase, form);
    }

    // Search button click
    if (searchBtn) {
      searchBtn.addEventListener('click', doSearch);
    }

    // Also trigger on Enter key inside the input
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); doSearch(); }
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Duplicate email check                                               */
  /* ------------------------------------------------------------------ */

  // Per-form email verification state
  var emailCheckState = { verified: false, checkedEmail: null };

  function resetEmailVerification(form) {
    emailCheckState.verified   = false;
    emailCheckState.checkedEmail = null;
    var statusEl = form.querySelector('#creg-email-check-status');
    var submitBtn = form.querySelector('.creg-submit-btn');
    if (statusEl) { statusEl.textContent = ''; statusEl.className = 'creg-email-check-status'; }
    if (submitBtn) submitBtn.disabled = true;
  }

  function setEmailVerified(form, email) {
    emailCheckState.verified     = true;
    emailCheckState.checkedEmail = email;
    var statusEl  = form.querySelector('#creg-email-check-status');
    var submitBtn = form.querySelector('.creg-submit-btn');
    if (statusEl) {
      statusEl.textContent = '✓ Email is available. You may proceed.';
      statusEl.className   = 'creg-email-check-status creg-email-check-ok';
    }
    if (submitBtn) submitBtn.disabled = false;
  }

  function setEmailDuplicate(form) {
    emailCheckState.verified   = false;
    emailCheckState.checkedEmail = null;
    var statusEl = form.querySelector('#creg-email-check-status');
    if (statusEl) {
      statusEl.textContent = '✕ This email is already registered. Please try a different email address.';
      statusEl.className   = 'creg-email-check-status creg-email-check-error';
    }
  }

  function initEmailCheck(form, apiBase) {
    var checkBtn  = form.querySelector('#creg-check-email-btn');
    var emailEl   = form.querySelector('#creg-email');
    var confirmEl = form.querySelector('#creg-emailConfirm');
    var submitBtn = form.querySelector('.creg-submit-btn');

    // Submit locked until email is verified
    if (submitBtn) submitBtn.disabled = true;

    // Reset verification whenever email or confirm changes
    [emailEl, confirmEl].forEach(function (el) {
      if (!el) return;
      el.addEventListener('input', function () { resetEmailVerification(form); });
    });

    if (!checkBtn) return;

    checkBtn.addEventListener('click', function () {
      var values = collectValues(form);
      var emailVal   = (values.email || '').trim();
      var confirmVal = (values.emailConfirm || '').trim();
      var statusEl   = form.querySelector('#creg-email-check-status');

      // Basic validation before hitting the API
      if (!emailVal || !EMAIL_RE.test(emailVal)) {
        if (statusEl) {
          statusEl.textContent = 'Please enter a valid email address first.';
          statusEl.className   = 'creg-email-check-status creg-email-check-error';
        }
        return;
      }
      if (confirmVal && emailVal !== confirmVal) {
        if (statusEl) {
          statusEl.textContent = 'Email addresses do not match.';
          statusEl.className   = 'creg-email-check-status creg-email-check-error';
        }
        return;
      }

      checkBtn.disabled    = true;
      checkBtn.textContent = 'Checking\u2026';
      if (statusEl) { statusEl.textContent = ''; statusEl.className = 'creg-email-check-status'; }

      var url = (apiBase || '').replace(/\/$/, '') + '/api/p21/customer/check-duplicate-email?email=' + encodeURIComponent(emailVal);

      fetch(url, {
        method:  'GET',
        headers: { 'Content-Type': 'application/json' }
      })
        .then(function (res) {
          return res.json().then(function (data) { return { ok: res.ok, data: data }; });
        })
        .then(function (r) {
          checkBtn.disabled    = false;
          checkBtn.textContent = 'Check Email';
          if (!r.ok || r.data.isDuplicate || r.data.exists) {
            setEmailDuplicate(form);
          } else {
            setEmailVerified(form, emailVal);
          }
        })
        .catch(function (err) {
          checkBtn.disabled    = false;
          checkBtn.textContent = 'Check Email';
          if (statusEl) {
            statusEl.textContent = 'Could not verify email. Please check your connection and try again.';
            statusEl.className   = 'creg-email-check-status creg-email-check-error';
          }
        });
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Submit Status Modal                                                 */
  /* ------------------------------------------------------------------ */

  var submitModal = null; // lazily resolved

  function getModal() {
    if (!submitModal) submitModal = document.getElementById('creg-submit-modal');
    return submitModal;
  }

  function openModal(state) {
    var m = getModal();
    if (!m) return;
    m.className = 'creg-modal-backdrop creg-modal--open creg-modal--' + state;
  }

  function setModalState(state) {
    var m = getModal();
    if (!m) return;
    m.className = 'creg-modal-backdrop creg-modal--open creg-modal--' + state;
  }

  function closeModal() {
    var m = getModal();
    if (!m) return;
    m.className = 'creg-modal-backdrop';
  }

  /* ------------------------------------------------------------------ */
  /*  Submission                                                          */
  /* ------------------------------------------------------------------ */

  var submitting = false;

  // Retained for possible future use (e.g. displaying/using the returned
  // registrationId for a status-check feature). Never rendered prominently.
  var lastRegistrationId = null;

  function wireOnce(buttonId, handler) {
    var btn = document.getElementById(buttonId);
    if (!btn) return;
    var clone = btn.cloneNode(true);
    btn.parentNode.replaceChild(clone, btn);
    clone.addEventListener('click', handler);
  }

  /**
   * Maps the existing wholesale form's collected values into the exact
   * field names expected by POST /api/customer-registration.
   * Address Information (streetAddress/city/stateProvince/postalCode/country)
   * is always required and always included. businessName / einTaxId are only
   * included when the applicant owns or represents a business.
   * businessType is always sent (per the documented contract) — defaults to
   * "barber" when the applicant has not selected "Yes" to "Do you own or
   * represent a business?"; otherwise the selected Business Type
   * (Barbershop / Salon Owner or Wholesaler / Distributor) is used.
   * Barbershop / Salon Owner-specific fields (barberCount,
   * currentlyRetailsProducts, whereShopNow, interestedInSellingProducts,
   * shopFrequency, shopArrangement) are only included when businessType is
   * "barbershop". currentlyRetailsProducts / interestedInSellingProducts are
   * sent as the literal strings "true"/"false" per the API contract (the UI
   * toggle buttons use "yes"/"no" internally and are converted here).
   */
  function mapToCustomerRegistrationRequest(values, attachments) {
    var ownsOrRepresentsBusiness = values.hasBusiness === 'yes';
    var isBarbershop = ownsOrRepresentsBusiness && values.businessType === 'barbershop';

    function yesNoToBoolString(v) {
      if (v === 'yes') return 'true';
      if (v === 'no') return 'false';
      return undefined;
    }

    var request = {
      businessType:             ownsOrRepresentsBusiness ? values.businessType : 'barber',
      firstName:                (values.firstName || '').trim(),
      lastName:                 (values.lastName || '').trim(),
      phone:                    (values.phone || '').trim(),
      email:                    (values.email || '').trim(),
      ownsOrRepresentsBusiness: ownsOrRepresentsBusiness,
      country:                  values.country || 'United States',

      // Address Information is always required, regardless of hasBusiness.
      streetAddress1:           (values.streetAddress || '').trim(),
      streetAddress2:           (values.streetAddress1 || '').trim(),
      city:                     (values.city || '').trim(),
      stateProvinceRegion:      (values.stateProvince || '').trim(),
      postalCode:               (values.postalCode || '').trim()
    };

    var socialMedia = (values.socialMedia || '').trim();
    if (socialMedia) request.socialMedia = socialMedia;

    var priceLibraryId = (values.priceLibraryId || '').trim();
    if (priceLibraryId) request.priceLibraryId = priceLibraryId;

    if (ownsOrRepresentsBusiness) {
      request.businessName         = (values.businessName || '').trim();
      request.einTaxId             = (values.taxId || '').trim() || undefined;
    }

    if (isBarbershop) {
      request.barberCount                 = (values.barberCount || '').trim();
      request.currentlyRetailsProducts    = yesNoToBoolString(values.currentlyRetailsProducts);
      if (values.currentlyRetailsProducts === 'yes') {
        request.whereShopNow = (values.whereShopNow || '').trim();
      } else if (values.currentlyRetailsProducts === 'no') {
        request.interestedInSellingProducts = yesNoToBoolString(values.interestedInSellingProducts);
      }
      request.shopFrequency   = values.shopFrequency;
      request.shopArrangement = values.shopArrangement;
    }

    var files = (attachments || []).map(function (a) { return a.file; });

    return { request: request, files: files };
  }

  /**
   * Real Customer Registration submission (formType === 'customer').
   * Uses the dedicated API module (customer-registration-api.js) which
   * builds FormData and calls POST /api/customer-registration.
   */
  function handleCustomerRegistrationSubmit(form, apiBase) {
    var result = validateAll(form, 'customer');

    Object.keys(result.errors).forEach(function (name) {
      touched[name] = true;
      showError(name, result.errors[name]);
    });
    result.requiredFields.forEach(function (name) {
      if (!result.errors[name]) clearError(name);
    });
    if (!result.errors.verificationFiles) clearError('verificationFiles');

    if (!result.valid) {
      var firstErrEl = form.querySelector('.creg-error-msg.creg-visible');
      if (firstErrEl) firstErrEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    var mapped = mapToCustomerRegistrationRequest(result.values, getAllVerificationAttachments(result.values));
    var btn    = form.querySelector('.creg-submit-btn');

    submitting = true;
    if (btn) { btn.disabled = true; btn.textContent = 'Submitting\u2026'; }
    hideBanner();
    openModal('submitting');

    window.CustomerRegistrationAPI
      .submitCustomerRegistration(apiBase, mapped.request, mapped.files)
      .then(function (outcome) {
        submitting = false;
        if (btn) { btn.disabled = false; btn.textContent = btn.getAttribute('data-label') || 'Submit Registration'; }

        if (outcome.status === 'success') {
          lastRegistrationId = outcome.data && outcome.data.registrationId;

          setModalState('success');
          wireOnce('creg-modal-done-btn', function () {
            closeModal();
            resetCustomerForm(form);
          });
          return;
        }

        if (outcome.status === 'conflict') {
          lastRegistrationId = outcome.data && outcome.data.registrationId;

          setModalState('conflict');
          wireOnce('creg-modal-conflict-done-btn', function () {
            closeModal();
          });
          return;
        }

        if (outcome.status === 'validationError') {
          // Keep form data intact; just surface the error near submission.
          var errorDetailEl = document.getElementById('creg-modal-error-detail');
          if (errorDetailEl) {
            var html = '<strong>' + escHtml(outcome.message) + '</strong>';
            if (outcome.errors && typeof outcome.errors === 'object') {
              html += '<ul>';
              Object.keys(outcome.errors).forEach(function (field) {
                var msgs = outcome.errors[field];
                if (Array.isArray(msgs)) {
                  msgs.forEach(function (m) {
                    html += '<li>' + escHtml(field) + ': ' + escHtml(m) + '</li>';
                  });
                }
              });
              html += '</ul>';
            }
            errorDetailEl.innerHTML = html;
          }
          setModalState('error');
          wireOnce('creg-modal-retry-btn', function () {
            closeModal();
            var detailEl = document.getElementById('creg-modal-error-detail');
            if (detailEl) detailEl.innerHTML = '';
          });
          return;
        }

        // networkError / unexpected status — generic, non-technical message.
        var genericDetailEl = document.getElementById('creg-modal-error-detail');
        if (genericDetailEl) genericDetailEl.innerHTML = '';
        setModalState('error');
        wireOnce('creg-modal-retry-btn', function () {
          closeModal();
          var detailEl = document.getElementById('creg-modal-error-detail');
          if (detailEl) detailEl.innerHTML = '';
        });
      });
  }

  function resetCustomerForm(form) {
    form.reset();
    fileState.file = null;
    touched = {};
    var preview = byId('creg-file-preview');
    if (preview) preview.style.display = 'none';
    var zoneLabel = form.querySelector('.creg-file-zone-label');
    if (zoneLabel) zoneLabel.style.display = '';
    var zoneSub = form.querySelector('.creg-file-zone-sub');
    if (zoneSub) zoneSub.style.display = '';
    form.querySelectorAll('.creg-error-msg').forEach(function (el) {
      el.textContent = '';
      el.classList.remove('creg-visible');
    });
    form.querySelectorAll('.creg-input--error, .creg-select--error').forEach(function (el) {
      el.classList.remove('creg-input--error', 'creg-select--error');
    });
    clearBusinessSelection(form);
    resetVerificationAttachments(form);
    setHasBusinessValue(form, 'no');
    touched.hasBusiness = false;
    setToggleValue(form, 'currentlyRetailsProducts', '');
    setToggleValue(form, 'interestedInSellingProducts', '');
    touched.currentlyRetailsProducts = false;
    touched.interestedInSellingProducts = false;
    refreshRetailSubFields(form);
    refreshBarbershopVisibility(form);
    var refElClear = document.getElementById('creg-modal-ref');
    if (refElClear) refElClear.textContent = '';
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function handleSubmit(form, apiBase, formType) {
    if (submitting) return;

    if (formType === 'customer') {
      handleCustomerRegistrationSubmit(form, apiBase);
      return;
    }

    // Email must be verified before submission
    if (!emailCheckState.verified) {
      var statusEl = form.querySelector('#creg-email-check-status');
      if (statusEl) {
        statusEl.textContent = 'Please check your email availability before submitting.';
        statusEl.className   = 'creg-email-check-status creg-email-check-error';
        statusEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    var result = validateAll(form, formType);

    // Mark all fields as touched and display errors
    Object.keys(result.errors).forEach(function (name) {
      touched[name] = true;
      showError(name, result.errors[name]);
    });

    // Clear errors for valid fields
    result.requiredFields.forEach(function (name) {
      if (!result.errors[name]) clearError(name);
    });

    if (!result.valid) {
      // Scroll to first error
      var firstErrEl = form.querySelector('.creg-error-msg.creg-visible');
      if (firstErrEl) firstErrEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    var payload = buildPayload(formType, result.values, fileState, []);
    var url     = (apiBase || '').replace(/\/$/, '') + '/api/p21/customer-registration';
    var btn     = form.querySelector('.creg-submit-btn');

    console.log('[Registration] Submitting as formType="' + formType + '"');
    console.log('[Registration] POST ' + url);
    console.log('[Registration] Payload:', payload);

    submitting = true;
    if (btn) { btn.disabled = true; btn.textContent = 'Submitting\u2026'; }
    hideBanner();
    openModal('submitting');

    fetch(url, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(payload)
    })
      .then(function (res) {
        return res.text().then(function (text) {
          return { ok: res.ok, status: res.status, text: text };
        });
      })
      .then(function (r) {
        submitting = false;
        if (btn) { btn.disabled = false; btn.textContent = btn.getAttribute('data-label') || 'Submit Registration'; }

        console.log('[Registration] Response status:', r.status);
        try { console.log('[Registration] Response body:', JSON.parse(r.text)); }
        catch (e) { console.log('[Registration] Response body (raw):', r.text); }

        if (r.ok) {
          var body = {};
          try { body = JSON.parse(r.text); } catch (e) { /* not JSON */ }
          var regId = body.registrationId || body.id || '';

          var refEl = document.getElementById('creg-modal-ref');
          if (refEl) refEl.textContent = regId ? 'Reference ID: ' + regId : '';

          setModalState('success');

          // Wire up Done button (replace to avoid duplicate listeners)
          var doneBtn = document.getElementById('creg-modal-done-btn');
          if (doneBtn) {
            var newDoneBtn = doneBtn.cloneNode(true);
            doneBtn.parentNode.replaceChild(newDoneBtn, doneBtn);
            newDoneBtn.addEventListener('click', function () {
              closeModal();
              // Reset the form completely
              form.reset();
              fileState.file = null;
              touched = {};
              var preview = byId('creg-file-preview');
              if (preview) preview.style.display = 'none';
              var zoneLabel = form.querySelector('.creg-file-zone-label');
              if (zoneLabel) zoneLabel.style.display = '';
              var zoneSub = form.querySelector('.creg-file-zone-sub');
              if (zoneSub) zoneSub.style.display = '';
              // Clear all inline errors
              form.querySelectorAll('.creg-error-msg').forEach(function (el) {
                el.textContent = '';
                el.classList.remove('creg-visible');
              });
              form.querySelectorAll('.creg-input--error, .creg-select--error').forEach(function (el) {
                el.classList.remove('creg-input--error', 'creg-select--error');
              });
              // Reset email verification state
              resetEmailVerification(form);
              // Clear business selection badge
              clearBusinessSelection(form);
              // Reset verification attachments and business toggle (wholesale form)
              resetVerificationAttachments(form);
              if (formType === 'customer') { setHasBusinessValue(form, 'no'); touched.hasBusiness = false; }
              // Clear modal detail fields
              var refElClear = document.getElementById('creg-modal-ref');
              if (refElClear) refElClear.textContent = '';
              // Scroll to top
              window.scrollTo({ top: 0, behavior: 'smooth' });
            });
          }
        } else {
          var errBody = {};
          try { errBody = JSON.parse(r.text); } catch (e) { /* not JSON */ }

          var errMsg = errBody.message || ('Submission failed (HTTP ' + r.status + ').');
          var errorDetailEl = document.getElementById('creg-modal-error-detail');
          if (errorDetailEl) {
            var html = '<strong>' + errMsg + '</strong>';
            if (errBody.errors && typeof errBody.errors === 'object') {
              html += '<ul>';
              Object.keys(errBody.errors).forEach(function (field) {
                var msgs = errBody.errors[field];
                if (Array.isArray(msgs)) {
                  msgs.forEach(function (m) {
                    html += '<li>' + field + ': ' + m + '</li>';
                  });
                }
              });
              html += '</ul>';
            }
            errorDetailEl.innerHTML = html;
          }

          setModalState('error');

          // Wire up Try Again button
          var retryBtn = document.getElementById('creg-modal-retry-btn');
          if (retryBtn) {
            var newRetryBtn = retryBtn.cloneNode(true);
            retryBtn.parentNode.replaceChild(newRetryBtn, retryBtn);
            newRetryBtn.addEventListener('click', function () {
              closeModal();
              var detailEl = document.getElementById('creg-modal-error-detail');
              if (detailEl) detailEl.innerHTML = '';
            });
          }
        }
      })
      .catch(function (err) {
        submitting = false;
        if (btn) { btn.disabled = false; btn.textContent = btn.getAttribute('data-label') || 'Submit Registration'; }
        console.error('[Registration] Network error:', err);

        var errorDetailEl = document.getElementById('creg-modal-error-detail');
        if (errorDetailEl) errorDetailEl.innerHTML = '<strong>Could not reach the server.</strong> Please check your connection and try again. (' + (err.message || 'fetch failed') + ')';

        setModalState('error');

        var retryBtn = document.getElementById('creg-modal-retry-btn');
        if (retryBtn) {
          var newRetryBtn = retryBtn.cloneNode(true);
          retryBtn.parentNode.replaceChild(newRetryBtn, retryBtn);
          newRetryBtn.addEventListener('click', function () {
            closeModal();
            var detailEl = document.getElementById('creg-modal-error-detail');
            if (detailEl) detailEl.innerHTML = '';
          });
        }
      });
  }

  /* ------------------------------------------------------------------ */
  /*  Init                                                                */
  /* ------------------------------------------------------------------ */

  function initForm(formId, formType) {
    var form = document.getElementById(formId);
    if (!form) return;

    var apiBase = form.getAttribute('data-api-base') || API_BASE_URL;

    attachBlurHandlers(form, formType);
    initFileInput(form);
    // Customer Registration no longer requires a pre-submit "Check Email" step.
    if (formType !== 'customer') {
      initEmailCheck(form, apiBase);
    }
    initBusinessSearch(form, apiBase);

    if (formType === 'customer') {
      initHasBusinessToggle(form);
      initBarbershopQuestions(form);
      initVerificationUploads(form);
    }

    var btn = form.querySelector('.creg-submit-btn');
    if (btn) btn.setAttribute('data-label', btn.textContent);

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      handleSubmit(form, apiBase, formType);
    });
  }

  function init() {
    initForm('customer-registration-form', 'customer');
    initForm('user-registration-form',     'user');
  }

  // Run after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
